import React, { useEffect } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import CrmPrepaidLinkModal from "./CrmPrepaidLinkModal";
import { useCrmPrepaidSession, isSessionLocked } from "./store/crmPrepaidSessionStore";

const STAY_ON_PAGE_MESSAGE =
  "Payment in progress — please stay on this page until the customer pays.";

const formatINR = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

// Minimised pill — sits above the cart Offcanvas (1045) and the modal (1055) so it stays
// reachable wherever the agent is on the lead page.
function CrmPrepaidMiniBar() {
  const customerDetails = useCrmPrepaidSession((s) => s.customerDetails);
  const payload = useCrmPrepaidSession((s) => s.payload);
  const phase = useCrmPrepaidSession((s) => s.phase);
  const restore = useCrmPrepaidSession((s) => s.restore);

  // Once auto-polling has stopped, saying "Awaiting payment" with a live dot would claim we
  // are still checking. Say it's paused and hold the dot still.
  const isPaused = phase === "stalled";

  // Show what the customer actually pays, i.e. the modal's "Total amount to be paid"
  // (Payable + the ₹50 sub-600 shipping charge order-service adds) — not the bare payable.
  const cartTotal = Number(payload?.payableAmount) || 0;
  const shippingCharges = cartTotal > 0 && cartTotal < 600 ? 50 : 0;
  const totalToCollect = cartTotal + shippingCharges;

  return (
    <div
      style={{
        position: "fixed",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1080,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 10px 8px 16px",
        borderRadius: 999,
        background: isPaused
          ? "linear-gradient(135deg,#64748b 0%,#94a3b8 100%)"
          : "linear-gradient(135deg,#0ea5e9 0%,#6366f1 100%)",
        color: "#fff",
        boxShadow: "0 10px 28px rgba(15,23,42,.3)",
      }}
    >
      <style>{`
        @keyframes psMiniPulse { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
      `}</style>
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: "#fff",
          animation: isPaused ? "none" : "psMiniPulse 1.4s ease-in-out infinite",
          opacity: isPaused ? 0.6 : 1,
        }}
      />
      <span style={{ fontSize: 13, fontWeight: 800 }}>
        {isPaused ? "Paused — open to check" : "Awaiting payment"}
      </span>
      <span style={{ fontSize: 12.5, opacity: 0.9 }}>
        {customerDetails?.first_name || "Customer"} ·{" "}
        {formatINR(totalToCollect)} to collect
      </span>
      {!isPaused && (
        <span style={{ fontSize: 11.5, opacity: 0.8 }}>
          Don&apos;t leave or refresh this page
        </span>
      )}
      <Button
        size="sm"
        variant="light"
        style={{ borderRadius: 999, fontWeight: 700, fontSize: 12 }}
        onClick={restore}
      >
        Open
      </Button>
    </div>
  );
}

// Mounted once from MainLayout so the prepaid session outlives the cart Offcanvas.
function CrmPrepaidSessionHost() {
  const open = useCrmPrepaidSession((s) => s.open);
  const minimized = useCrmPrepaidSession((s) => s.minimized);
  const phase = useCrmPrepaidSession((s) => s.phase);

  const locked = open && isSessionLocked(phase);

  // While the link is being sent / payment is being awaited, block everything that would
  // destroy the live session: reload, tab close, in-app link clicks and browser back/forward.
  useEffect(() => {
    if (!locked) return undefined;

    // Browser reload button / tab close — native confirmation dialog.
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    // Ctrl/Cmd+R and F5 intercepted early so we can surface an in-app warning too.
    const handleKeyDown = (e) => {
      const isReloadKey =
        e.key === "F5" || ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"));
      if (isReloadKey) {
        e.preventDefault();
        toast.warning(
          "Please don't refresh — we're waiting for the payment to complete."
        );
      }
    };

    // Capture on `document` runs before React's root-container listener, so stopping the
    // event here also cancels react-router <Link>/<NavLink> navigation.
    const handleNavClick = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      // The sidebar's top-level rail items navigate programmatically from a
      // <div onClick={() => navigate(...)}> (NavbarVerticalMenu), which an anchor-only
      // check can't see — so block every click inside the sidebar. Hover still opens the
      // panel; only the navigation itself is refused.
      if (!target.closest("aside.sidebar")) {
        const anchor = target.closest("a[href]");
        if (!anchor) return;
        const href = anchor.getAttribute("href") || "";
        // Bootstrap accordions/dropdowns use href="#"; new tabs don't leave this page.
        if (href.startsWith("#") || href.toLowerCase().startsWith("javascript:")) return;
        if (anchor.getAttribute("target") === "_blank") return;
      }

      e.preventDefault();
      e.stopPropagation();
      toast.warning(STAY_ON_PAGE_MESSAGE);
    };

    // Back/forward: keep re-pushing the current entry so the URL never changes.
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      toast.warning(STAY_ON_PAGE_MESSAGE);
    };
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleNavClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleNavClick, true);
      // The extra history entry is left in place on purpose — popping it here would itself
      // navigate the agent away.
    };
  }, [locked]);

  if (!open) return null;

  return (
    <>
      <CrmPrepaidLinkModal />
      {minimized && <CrmPrepaidMiniBar />}
    </>
  );
}

export default CrmPrepaidSessionHost;
